import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import type { MentionExpandContext, MentionSearchResult, MentionUser } from "@/components/AtMention/types";
import useCapabilities, { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import useMentionSearch from "@/hooks/useMentionSearch";
import {
  facilityMatchesQuery,
  noteTitleMatchesQuery,
  personNameMatchesQuery,
  workplaceMatchesQuery
} from "@/lib/geo/quickAddMentionMatch";
import { rankQuickAddMentions, type GeoProximityContext } from "@/lib/geo/rankQuickAddMentions";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import {
  EVENTS_FOR_REFERENCE_SEARCH_QUERY,
  type EventsForReferenceSearchResponse,
  type EventsForReferenceSearchVariables
} from "@/pages/tasks/[taskId]/_hooks/eventsForReferenceSearchQuery";
import { useReferencePickerItems } from "@/pages/tasks/[taskId]/_hooks/useReferencePickerItems";
import type { Section } from "@/types";
import {
  ME_CAREER_CONSULTANT_SECTIONS_QUERY,
  QUICK_ADD_FUZZY_FACILITIES_SEARCH_QUERY,
  QUICK_ADD_FUZZY_PEOPLE_SEARCH_QUERY,
  QUICK_ADD_MENTION_HITS_PER_PAGE,
  QUICK_ADD_PEOPLE_MEMBERSHIP_QUERY,
  QUICK_ADD_WORKPLACE_PEOPLE_QUERY,
  QUICK_ADD_WORKPLACE_PEOPLE_STATUSES,
  type MeCareerConsultantSectionsResponse,
  type QuickAddFacilitySearchNode,
  type QuickAddFuzzyFacilitiesSearchResponse,
  type QuickAddFuzzyFacilitiesSearchVariables,
  type QuickAddFuzzyPeopleSearchResponse,
  type QuickAddFuzzyPeopleSearchVariables,
  type QuickAddPeopleMembershipResponse,
  type QuickAddPeopleMembershipVariables,
  type QuickAddPersonSearchNode,
  type QuickAddWorkplacePeopleResponse,
  type QuickAddWorkplacePeopleVariables
} from "./queries";
import {
  facilityMentionFromPersonNode,
  mapEventToMention,
  mapPickerItemToMention,
  mapQuickAddFacilityNodeToMention,
  mapQuickAddPersonNodeToMention,
  mergePersonMembership,
  QUICK_ADD_MENTION_OVERLAY,
  teammateMention,
  toLookupContext
} from "./quickAddMentionMappers";

const EVENT_SEARCH_LIMIT = 10;
const EMPTY_FACILITY_IDS: string[] = [];
const EMPTY_PGA_IDS: string[] = [];

function mentionDisplayName(user: MentionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
}

function isCompactAcronymQuery(query: string): boolean {
  const compact = query.trim().toLowerCase();
  return !compact.includes(" ") && /^[a-z]{3,6}$/.test(compact);
}

function uniqueMentions(users: MentionUser[]): MentionUser[] {
  const byId = new Map<string, MentionUser>();
  for (const user of users) {
    if (!user.id || byId.has(user.id)) continue;
    byId.set(user.id, user);
  }
  return [...byId.values()];
}

function uniquePeople(nodes: QuickAddPersonSearchNode[]): QuickAddPersonSearchNode[] {
  const byId = new Map<string, QuickAddPersonSearchNode>();
  for (const node of nodes) {
    const key = node.pgaId || node.id;
    if (!key || byId.has(key)) continue;
    byId.set(key, node);
  }
  return [...byId.values()];
}

function withSyntheticFacility(
  people: QuickAddPersonSearchNode[],
  mapped: MentionUser[],
  query: string
): MentionUser[] {
  const hasMatchingFacility = mapped.some(
    (user) => user.kind === "facility" && facilityMatchesQuery(mentionDisplayName(user), query)
  );
  if (hasMatchingFacility) return mapped;
  for (const node of people) {
    const facilityName = node.primaryFacility?.name;
    if (!facilityName || !facilityMatchesQuery(facilityName, query)) continue;
    const synthetic = facilityMentionFromPersonNode(node);
    if (synthetic) return [synthetic, ...mapped];
  }
  return mapped;
}

function keepRecord(user: MentionUser, query: string): boolean {
  if (user.kind === "facility") {
    return (
      facilityMatchesQuery(mentionDisplayName(user), query) || facilityMatchesQuery(user.facilityName || "", query)
    );
  }
  if (user.kind === "person") {
    if (personNameMatchesQuery(user, query)) return true;
    return workplaceMatchesQuery(user.facilityName, query);
  }
  if (user.kind === "event") {
    const full = mentionDisplayName(user).toLowerCase();
    const compactQuery = query.trim().toLowerCase();
    return full.includes(compactQuery) || compactQuery.split(/\s+/).every((token) => !token || full.includes(token));
  }
  return true;
}

function interleaveNotes(ranked: MentionUser[], notes: MentionUser[]): MentionUser[] {
  if (notes.length === 0) return ranked;
  const index = ranked.findIndex((user) => user.kind === "person" || user.kind === "facility" || user.kind === "event");
  if (index < 0) return [...ranked, ...notes].slice(0, 12);
  return [...ranked.slice(0, index + 1), ...notes, ...ranked.slice(index + 1)].slice(0, 12);
}

type PendingSelect = { context: MentionExpandContext; query: string };

export default function useQuickAddMentionSearch(query: string): MentionSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [pendingSelect, setPendingSelect] = useState<PendingSelect | null>(null);

  if (pendingSelect && pendingSelect.query !== query) {
    setPendingSelect(null);
  }
  const pendingContext = pendingSelect?.query === query ? pendingSelect.context : null;

  useEffect(() => {
    if (!query) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { users: teammateUsers, isLoading: teammatesLoading } = useMentionSearch(query);

  const { sectionLegacyIds } = useCapabilities();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewPersonMembership = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonMembership
  });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });

  const trimmedQuery = debouncedQuery.trim();
  const skipRankingQueries = !trimmedQuery;
  const skipPeople = skipRankingQueries || !canViewPeople;
  const skipFacilities = skipRankingQueries || !canViewFacilities;

  const { data: consultantData } = useQuery<MeCareerConsultantSectionsResponse>(ME_CAREER_CONSULTANT_SECTIONS_QUERY, {
    skip: skipRankingQueries,
    errorPolicy: "all",
    fetchPolicy: "cache-first"
  });

  const { data: sectionsData } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: skipRankingQueries || sectionLegacyIds.length === 0,
    fetchPolicy: "cache-first"
  });

  const { data: eventsData, loading: eventsLoading } = useQuery<
    EventsForReferenceSearchResponse,
    EventsForReferenceSearchVariables
  >(EVENTS_FOR_REFERENCE_SEARCH_QUERY, {
    variables: { query: { title: debouncedQuery }, first: EVENT_SEARCH_LIMIT },
    skip: !debouncedQuery || !!pendingContext
  });

  const {
    data: peopleData,
    loading: peopleLoading,
    error: peopleError
  } = useQuery<QuickAddFuzzyPeopleSearchResponse, QuickAddFuzzyPeopleSearchVariables>(
    QUICK_ADD_FUZZY_PEOPLE_SEARCH_QUERY,
    {
      variables: { search: trimmedQuery, first: QUICK_ADD_MENTION_HITS_PER_PAGE },
      skip: skipPeople,
      fetchPolicy: "no-cache",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true
    }
  );

  const {
    data: facilitiesData,
    loading: facilitiesLoading,
    error: facilitiesError
  } = useQuery<QuickAddFuzzyFacilitiesSearchResponse, QuickAddFuzzyFacilitiesSearchVariables>(
    QUICK_ADD_FUZZY_FACILITIES_SEARCH_QUERY,
    {
      variables: { search: trimmedQuery, first: QUICK_ADD_MENTION_HITS_PER_PAGE },
      skip: skipFacilities,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true
    }
  );

  const facilityNodes = useMemo(
    (): QuickAddFacilitySearchNode[] =>
      skipFacilities || facilitiesError ? [] : (facilitiesData?.fuzzySearchFacilities?.nodes ?? []),
    [facilitiesData, facilitiesError, skipFacilities]
  );

  const workplaceFacilityIds = useMemo(() => {
    const ids = facilityNodes
      .filter((node) => facilityMatchesQuery(node.name, trimmedQuery))
      .map((node) => node.id)
      .filter(Boolean)
      .slice(0, 2);
    return ids.length > 0 ? ids : EMPTY_FACILITY_IDS;
  }, [facilityNodes, trimmedQuery]);

  const skipWorkplace = skipPeople || isCompactAcronymQuery(trimmedQuery) || workplaceFacilityIds.length === 0;

  const {
    data: workplaceData,
    loading: workplaceLoading,
    error: workplaceError
  } = useQuery<QuickAddWorkplacePeopleResponse, QuickAddWorkplacePeopleVariables>(QUICK_ADD_WORKPLACE_PEOPLE_QUERY, {
    variables: {
      input: {
        facilityIds: workplaceFacilityIds,
        statuses: [...QUICK_ADD_WORKPLACE_PEOPLE_STATUSES]
      },
      first: Math.max(workplaceFacilityIds.length, 1) * QUICK_ADD_MENTION_HITS_PER_PAGE
    },
    skip: skipWorkplace,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const fuzzyPersonNodes = useMemo(
    (): QuickAddPersonSearchNode[] => (skipPeople || peopleError ? [] : (peopleData?.fuzzySearchPeople?.nodes ?? [])),
    [peopleData, peopleError, skipPeople]
  );

  const workplacePersonNodes = useMemo(
    (): QuickAddPersonSearchNode[] => (skipWorkplace || workplaceError ? [] : (workplaceData?.people?.nodes ?? [])),
    [skipWorkplace, workplaceData, workplaceError]
  );

  const personNodes = useMemo(
    () => uniquePeople([...fuzzyPersonNodes, ...workplacePersonNodes]),
    [fuzzyPersonNodes, workplacePersonNodes]
  );

  const personPgaIds = useMemo(() => {
    const ids = personNodes.map((node) => node.pgaId?.trim()).filter((id): id is string => Boolean(id));
    return ids.length > 0 ? ids : EMPTY_PGA_IDS;
  }, [personNodes]);

  const skipMembership = skipPeople || !canViewPersonMembership || personPgaIds.length === 0;

  const {
    data: membershipData,
    loading: membershipLoading,
    error: membershipError
  } = useQuery<QuickAddPeopleMembershipResponse, QuickAddPeopleMembershipVariables>(QUICK_ADD_PEOPLE_MEMBERSHIP_QUERY, {
    variables: {
      input: { pgaIds: personPgaIds },
      first: Math.max(personPgaIds.length, 1)
    },
    skip: skipMembership,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const membershipNodes = skipMembership || membershipError ? undefined : membershipData?.people?.nodes;
  const membershipReady = Array.isArray(membershipNodes);
  const hidePeopleUntilMembership = !skipMembership && membershipLoading;
  const membershipPgaIds = useMemo(() => {
    const ids = new Set<string>();
    if (!membershipReady || !membershipNodes) return ids;
    for (const node of membershipNodes) {
      const id = node.pgaId?.trim();
      if (id) ids.add(id);
    }
    return ids;
  }, [membershipNodes, membershipReady]);
  const peopleWithMembership = useMemo(
    () => (membershipReady ? mergePersonMembership(personNodes, membershipNodes) : personNodes),
    [membershipNodes, membershipReady, personNodes]
  );
  const peopleByPgaId = useMemo(() => {
    const byId = new Map<string, QuickAddPersonSearchNode>();
    for (const node of peopleWithMembership) {
      const key = node.pgaId || node.id;
      if (key) byId.set(key, node);
    }
    return byId;
  }, [peopleWithMembership]);
  const mapPersonNode = useCallback(
    (node: QuickAddPersonSearchNode) => {
      const pgaId = node.pgaId?.trim();
      return mapQuickAddPersonNodeToMention(peopleByPgaId.get(node.pgaId || node.id) ?? node, {
        dropBlankProgramStatus: Boolean(membershipReady && pgaId && membershipPgaIds.has(pgaId))
      });
    },
    [membershipPgaIds, membershipReady, peopleByPgaId]
  );

  const geoContext: GeoProximityContext = useMemo(() => {
    const assignedIds = new Set(sectionLegacyIds);
    const assignedSectionNames = (sectionsData?.sections ?? [])
      .filter(
        (section) => Boolean(section.legacyId && assignedIds.has(section.legacyId)) || assignedIds.has(section.id)
      )
      .map((section) => section.name);
    return {
      consultantSectionNames: consultantData?.careerConsultantSections?.map((section) => section.name) ?? [],
      assignedSectionNames
    };
  }, [consultantData, sectionLegacyIds, sectionsData]);

  const recordMentions = useMemo(() => {
    const fuzzyPeople = hidePeopleUntilMembership
      ? []
      : fuzzyPersonNodes.map(mapPersonNode).filter((item): item is MentionUser => item !== null);
    const workplacePeople = hidePeopleUntilMembership
      ? []
      : workplacePersonNodes
          .map(mapPersonNode)
          .filter((item): item is MentionUser => item !== null)
          .filter((user) => keepRecord(user, query));
    const facilities = facilityNodes
      .map(mapQuickAddFacilityNodeToMention)
      .filter((item): item is MentionUser => item !== null);
    return uniqueMentions(
      withSyntheticFacility(personNodes, [...fuzzyPeople, ...workplacePeople, ...facilities], query)
    );
  }, [
    facilityNodes,
    fuzzyPersonNodes,
    hidePeopleUntilMembership,
    mapPersonNode,
    personNodes,
    query,
    workplacePersonNodes
  ]);

  const rankedBase = useMemo(() => {
    const teammates = teammateUsers
      .map(teammateMention)
      .filter((user) => personNameMatchesQuery(user, query) || workplaceMatchesQuery(user.facilityName, query));
    const events = (eventsData?.events?.edges ?? [])
      .map(({ node }) => mapEventToMention(node))
      .filter((user) => keepRecord(user, query));
    return rankQuickAddMentions([...teammates, ...recordMentions, ...events], query, geoContext);
  }, [eventsData, geoContext, query, recordMentions, teammateUsers]);

  const prefetchParent = useMemo(
    () =>
      rankedBase.find(
        (user) => user.expandContext && (user.kind === "person" || user.kind === "facility" || user.kind === "event")
      ) ?? null,
    [rankedBase]
  );

  const lookupContext = useMemo(() => {
    const prefetch = prefetchParent?.kind === "person" ? null : (prefetchParent?.expandContext ?? null);
    const context = pendingContext ?? prefetch;
    return context ? toLookupContext(context) : null;
  }, [pendingContext, prefetchParent]);

  const { items: pickerItems, loading: pickerLoading } = useReferencePickerItems(lookupContext);

  const autoInsertUser = useMemo(() => {
    if (!pendingContext) return null;
    const parent = pickerItems.find((item) => {
      if (pendingContext.type === "Person") return item.__typename === "CRMPerson";
      if (pendingContext.type === "Facility") return item.__typename === "CRMTaskFacility";
      return item.__typename === "CRMEvent";
    });
    return parent ? mapPickerItemToMention(parent) : null;
  }, [pendingContext, pickerItems]);

  const users = useMemo(() => {
    if (pendingContext) return rankedBase;
    const notes = pickerItems.filter((item) => item.__typename === "CRMNote");
    const uniqueNotes = [...new Map(notes.map((note) => [note.id, note])).values()];
    const matching = uniqueNotes.filter((note) => noteTitleMatchesQuery(note.displayName, query));
    const chosen = (matching.length > 0 ? matching : uniqueNotes.slice(0, 3)).slice(0, 5);
    const noteMentions = chosen.map(mapPickerItemToMention).filter((item): item is MentionUser => item !== null);
    return interleaveNotes(rankedBase, noteMentions);
  }, [pendingContext, pickerItems, query, rankedBase]);

  const resolveSelect = useCallback(
    (user: MentionUser) => {
      if (user.selectAction === "expand" && user.expandContext) {
        setPendingSelect({ context: user.expandContext, query });
        return false;
      }
      if (pendingContext) setPendingSelect(null);
      return true;
    },
    [pendingContext, query]
  );

  const dismiss = useCallback(() => {
    if (pendingSelect) {
      setPendingSelect(null);
      return false;
    }
    return true;
  }, [pendingSelect]);

  const personInsertPending = pendingContext?.type === "Person" && !autoInsertUser;
  const personLookupFailed = Boolean(personInsertPending && !pickerLoading);
  const entitySearchLoading =
    (!skipPeople && peopleLoading) ||
    (!skipFacilities && facilitiesLoading) ||
    (!skipWorkplace && workplaceLoading) ||
    (!skipMembership && membershipLoading) ||
    eventsLoading;
  const isLoading = personInsertPending
    ? pickerLoading
    : (teammatesLoading || entitySearchLoading) && users.length === 0;

  const peopleQueryFailed = !skipPeople && Boolean(peopleError) && fuzzyPersonNodes.length === 0;
  const facilitiesQueryFailed = !skipFacilities && Boolean(facilitiesError) && facilityNodes.length === 0;
  const entitySearchFailed =
    (peopleQueryFailed || skipPeople) &&
    (facilitiesQueryFailed || skipFacilities) &&
    (peopleQueryFailed || facilitiesQueryFailed);

  const overlay = personLookupFailed
    ? { ...QUICK_ADD_MENTION_OVERLAY, error: "Could not attach that person. Pick again." }
    : entitySearchFailed
      ? { ...QUICK_ADD_MENTION_OVERLAY, error: "Couldn't load people and facilities." }
      : QUICK_ADD_MENTION_OVERLAY;

  return {
    users,
    isLoading,
    resolveSelect,
    dismiss,
    overlay,
    autoInsertUser
  };
}
